<script setup>
import { ref, onMounted, computed, watch, watchEffect } from 'vue';
import { useI18n } from 'vue-i18n';
import useUserStore from '../stores/useUserStore';
import Dropdown from 'primevue/dropdown';
import Button from 'primevue/button';

const { t } = useI18n();
const userStore = useUserStore();

const users = ref([]);
const selectedUser = ref(null);
const isLoading = ref(false);

const isImpersonating = computed(() => userStore.isImpersonating);
const impersonatedEmail = computed(() => userStore.impersonationEmail);

const isAdmin = computed(() => userStore.userProfile?.role === 'admin');

onMounted(async () => {
    // Only fetch users if user is admin and NOT currently impersonating someone
    // (If impersonating, they lose admin privileges in the eyes of the backend usually)
    if (isAdmin.value && !isImpersonating.value) {
        isLoading.value = true;
        try {
            const fetchedUsers = await userStore.fetchAllUsers();
            // Ensure fetchedUsers is an array
            if (Array.isArray(fetchedUsers)) {
                users.value = fetchedUsers;
            } else if (fetchedUsers && typeof fetchedUsers === 'object') {
                // Handle case where data might be wrapped in an object
                 users.value = Object.values(fetchedUsers);
            } else {
                users.value = [];
            }
        } catch (error) {
            console.error('Failed to load users for impersonation:', error);
            users.value = [];
        } finally {
            isLoading.value = false;
        }
    }
});

const handleImpersonate = () => {
    if (selectedUser.value) {
        // Handle both object (with email property) and string (if API returns strings)
        const email = selectedUser.value.email || selectedUser.value;
        if (email && typeof email === 'string') {
             userStore.startImpersonation(email);
        }
    }
};

const handleStopImpersonation = () => {
    userStore.stopImpersonation();
};

watch(selectedUser, (newValue) => {
    if (newValue) {
        handleImpersonate();
    }
});
</script>

<template>
    <div class="impersonation-control flex items-center gap-2" v-if="isAdmin || isImpersonating">
        <template v-if="isImpersonating">
            <div class="impersonating-badge px-3 py-1 rounded bg-yellow-100 text-yellow-800 border border-yellow-200 flex items-center gap-2">
                <i class="pi pi-user-edit"></i>
                <span class="text-sm font-medium">Impersonating: {{ impersonatedEmail }}</span>
                <Button
                    icon="pi pi-times"
                    text
                    rounded
                    severity="danger"
                    size="small"
                    aria-label="Stop Impersonation"
                    @click="handleStopImpersonation"
                    v-tooltip.bottom="'Stop Impersonation'"
                    class="w-6 h-6 !p-0 flex items-center justify-center"
                />
            </div>
        </template>

        <template v-else-if="isAdmin">
            <Dropdown
                v-model="selectedUser"
                :options="users"
                optionLabel="email"
                filter
                placeholder="Impersonate User"
                class="w-full md:w-56 custom-dropdown"
                :loading="isLoading"
                emptyMessage="No users found"
                emptyFilterMessage="No users found"
            >
                <template #option="slotProps">
                    <div class="flex items-center">
                        <div>{{ slotProps.option.email || slotProps.option }}</div>
                        <div v-if="slotProps.option.name" class="ml-2 text-gray-500 text-xs">({{ slotProps.option.name }})</div>
                    </div>
                </template>
                 <template #value="slotProps">
                    <div v-if="slotProps.value" class="flex align-items-center">
                        <div>{{ slotProps.value.email || slotProps.value }}</div>
                    </div>
                    <span v-else>
                        {{ slotProps.placeholder }}
                    </span>
                </template>
            </Dropdown>
        </template>
    </div>
</template>

<style scoped>
.impersonation-control {
    z-index: 1000;
}

:deep(.custom-dropdown .p-dropdown-label) {
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
    font-size: 0.875rem;
}
</style>
